import { useCallback, useEffect, useRef, useState } from 'react'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import { I18nextProvider } from 'react-i18next'
import i18n from './i18n'
import { AppRoutes } from './router'
import {
  assertModelsConfigContract,
  getConfigBatch,
  CONFIG_KEYS,
  ModelsConfigContractError,
  type AppConfig,
} from '@/api/config'
import {
  getAccessToken,
  getRefreshToken,
  hydrateNativeSessionState,
  refreshSessionOnForeground,
  ApiError,
  type SessionHydrationState,
} from '@/api/client'
import { getMe } from '@/api/user'
import { setAppConfig } from '@/hooks/useAppConfig'
import { loginUser, logoutUser, setAuthInitialized } from '@/hooks/useAppStore'
import { useNetworkStatus } from '@/hooks/useNetworkStatus'
import NoNetworkScreen from '@/components/feature/NoNetworkScreen'
import VipExpiryNotice from '@/components/feature/VipExpiryNotice'
import NotificationToast from '@/components/feature/NotificationToast'
import TestEnvironmentBanner from '@/components/feature/TestEnvironmentBanner'
import IapLifecycleCoordinator from '@/components/feature/IapLifecycleCoordinator'
import { GenerationTaskProvider } from '@/contexts/GenerationTaskContext'
import { APP_BRAND_MARK_URL, APP_NAME } from '@/config/brand'

type BootstrapState = 'loading' | 'ready' | 'error' | 'config-error'

async function loadAppConfig(): Promise<AppConfig> {
  const data = await getConfigBatch(CONFIG_KEYS.ALL as unknown as string[])
  if (!data) throw new Error('app config is unavailable')

  const meta = data.glopic_meta
  const templates = data.glopic_templates
  const styles = data.glopic_styles
  const display = data.glopic_display
  const models = data.glopic_models
  const points = data.glopic_points
  assertModelsConfigContract(models)

  return {
    version: meta?.version ?? 1,
    force_update: meta?.force_update ?? false,
    hd_download_requires_vip: meta?.hd_download_requires_vip ?? true,
    free_prompt_expand_count: meta?.free_prompt_expand_count ?? 1,
    ultra_vip_only: meta?.ultra_vip_only ?? false,
    default_model: meta?.default_model,
    contact_url: meta?.contact_url ?? '',
    templates: templates?.templates ?? [],
    template_categories: templates?.template_categories ?? [],
    activity_templates: templates?.activity_templates ?? [],
    styles: styles?.styles ?? [],
    ratios: display?.ratios ?? [],
    tryon_models: display?.tryon_models ?? [],
    showcase_images: display?.showcase_images ?? [],
    models: models?.models ?? [],
    generation_entries: models?.entries ?? [],
    models_contract_version: models.contract_version,
    pricing_config_version: models?.pricing_config_version,
    pricing_config_sha256: models?.pricing_config_sha256,
    generation_entry_config_bundle_id: models?.generation_entry_config_bundle_id,
    points_rules: points
      ? {
          register_gift: points.register_gift,
          daily_login: points.daily_login,
          generate_cost: points.generate_cost,
          prompt_expand_cost: points.prompt_expand_cost,
          tool_costs: points.tool_costs ?? {},
          mode_costs: points.mode_costs ?? {},
        }
      : undefined,
    agreements: data.glopic_agreements ?? {},
    contact: data.glopic_contact ?? undefined,
  }
}

async function loadSession(): Promise<{
  hydration: SessionHydrationState
  userInfo?: Awaited<ReturnType<typeof getMe>>
}> {
  const hydration = await hydrateNativeSessionState()
  if (hydration.kind !== 'authenticated') return { hydration }
  try {
    const userInfo = await getMe()
    return { hydration, userInfo }
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return { hydration: { kind: 'anonymous' } }
    }
    throw error
  }
}

function LaunchLoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <img
          src={APP_BRAND_MARK_URL}
          alt={APP_NAME}
          className="h-28 w-28 rounded-[24px] object-contain"
        />
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-pink-200 border-t-pink-500" />
        <p className="text-sm font-medium text-gray-500">正在加载闪绘...</p>
      </div>
    </div>
  )
}

function LaunchErrorScreen({ onRetry, configError = false }: { onRetry: () => void; configError?: boolean }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8F7FF] px-6">
      <div className="w-full max-w-sm rounded-3xl bg-white p-7 text-center shadow-sm">
        <i className={`${configError ? 'ri-settings-3-line' : 'ri-wifi-off-line'} text-4xl text-pink-400`} />
        <h1 className="mt-4 text-lg font-bold text-gray-800">
          {configError ? '生图配置暂不可用' : '暂时无法完成启动'}
        </h1>
        <p className="mt-2 text-sm leading-6 text-gray-500">
          {configError
            ? '当前环境的生图模型配置版本不兼容，请稍后重试或联系客服。'
            : '登录状态和应用配置尚未同步，请检查网络后重试。'}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 w-full rounded-2xl bg-gradient-to-r from-[#FF6B9D] to-[#A855F7] py-3 text-sm font-bold text-white"
        >
          重新加载
        </button>
      </div>
    </div>
  )
}

function commitSession(session: Awaited<ReturnType<typeof loadSession>>) {
  if (session.hydration.kind === 'unavailable') throw new Error('session hydration unavailable')
  if (session.hydration.kind === 'anonymous' || !session.userInfo) {
    logoutUser()
    setAuthInitialized()
    return
  }
  const userInfo = session.userInfo
  loginUser(
    {
      userId: userInfo.user_id,
      nickname: userInfo.nickname,
      avatar: userInfo.avatar,
      phone: userInfo.phone ?? undefined,
      email: userInfo.email ?? undefined,
      points: userInfo.points,
      isVip: userInfo.is_vip,
      vipExpireTime: userInfo.vip_expire_time,
      vipEntitlement: userInfo.vip_entitlement ?? null,
    },
    {
      accessToken: getAccessToken()!,
      refreshToken: getRefreshToken()!,
    },
  )
}

function AppInner() {
  const navigate = useNavigate()
  const isOnline = useNetworkStatus()
  const [bootstrapState, setBootstrapState] = useState<BootstrapState>('loading')
  const bootstrapInFlight = useRef(false)

  const bootstrap = useCallback(async () => {
    if (bootstrapInFlight.current) return
    bootstrapInFlight.current = true
    setBootstrapState('loading')
    try {
      const [config, session] = await Promise.all([loadAppConfig(), loadSession()])
      setAppConfig(config)
      commitSession(session)
      setBootstrapState('ready')
    } catch (error) {
      console.error('app bootstrap failed', error)
      setBootstrapState(error instanceof ModelsConfigContractError ? 'config-error' : 'error')
    } finally {
      bootstrapInFlight.current = false
    }
  }, [])

  useEffect(() => {
    void bootstrap()

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || bootstrapInFlight.current) return
      void refreshSessionOnForeground().then(() => Promise.all([loadAppConfig(), loadSession()])).then(([config, session]) => {
        setAppConfig(config)
        commitSession(session)
      }).catch((error) => {
        console.warn('foreground snapshot refresh failed; keeping previous snapshot', error)
      })
    }
    const handleAuthLogout = () => navigate('/login', { replace: true })
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('auth:logout', handleAuthLogout as EventListener)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('auth:logout', handleAuthLogout as EventListener)
    }
  }, [bootstrap, navigate])

  if (isOnline === false) return <NoNetworkScreen />
  if (bootstrapState === 'loading') return <LaunchLoadingScreen />
  if (bootstrapState === 'error') return <LaunchErrorScreen onRetry={() => void bootstrap()} />
  if (bootstrapState === 'config-error') {
    return <LaunchErrorScreen configError onRetry={() => void bootstrap()} />
  }

  return (
    <GenerationTaskProvider>
      <TestEnvironmentBanner />
      <IapLifecycleCoordinator />
      <AppRoutes />
      <VipExpiryNotice />
      <NotificationToast />
    </GenerationTaskProvider>
  )
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <AppInner />
      </BrowserRouter>
    </I18nextProvider>
  )
}

export default App
